/*
 * Monitor video self-heal, V1: which route the Monitor's live screens take, chosen at RUN time.
 * (docs/MONITOR-VIDEO-SELFHEAL-DESIGN.md section 3, with the owner's answers of 2026-09-29.)
 *
 * WHY RUN TIME. lib/scrcpyStream.js reads REACT_APP_MONITOR_TRANSPORT behind
 * `typeof process !== 'undefined'`. CRA's DefinePlugin replaces `process.env.X` expressions but not
 * `typeof process`, which is 'undefined' in a browser - so in a PRODUCTION bundle that constant is
 * always 'local', whatever the build set. Every bundle ever deployed dials ws://localhost:6847 on the
 * viewer's own PC, which is why a customer without the relay sees black tiles. This module decides
 * instead, per page load.
 *
 * TRANSPORTS
 *   local  - today's path: ws://localhost:6847 (the relay on the viewer's own PC), today's quality.
 *   remote - through our server: wss://<host>/uiapi/bm/vview/{installId}?serial=..; the grid tiles
 *            stream at the lower server-route tier (owner D5: ~400 kbps / 10 fps / 360p).
 *   auto   - the owner's route (2026-09-29): a ~1 s probe of ws://localhost:6847. It answers -> direct
 *            (local) first, and any single phone whose direct socket fails before its first picture
 *            moves to the server route (a phone on another PC). No answer -> through our server.
 *
 * PRECEDENCE, per key (transport / reconnect / states / repair / hold / reprobe); the first source with a value wins:
 *   1. the URL: ?transport=local|remote|auto, ?reconnect=on|off, ?states=.., ?repair=..
 *      A URL value is also STORED (step 2), so a support instruction survives a reload; the value
 *      `default` (or reset/clear) removes the stored one. Deviation from the design text: the design
 *      had `auto` clear the stored value, but the owner made `auto` a real route, so clearing uses
 *      `default`. A stored value is always shown on the page with a Reset button.
 *   2. localStorage necro.monitor.<key> (every access in try/catch).
 *   3. the server: GET /uiapi/bm/video/config -> {transport, reconnect, states, repair,
 *      repairCooldownSec, remoteLow?}. This is the FLEET ROLLBACK with no new bundle.
 *   4. the build env REACT_APP_MONITOR_TRANSPORT (dev harnesses; transport only).
 *   5. the default: 'auto' in production, 'local' in development; switches on.
 *
 * OFF MUST MEAN TODAY (review M4):
 *   - 404 from the config endpoint = an older server -> today's behaviour for every key (local, no
 *     reconnect, no states, no repair). So a Spring rollback also rolls the Monitor back.
 *   - network error / 5xx / timeout (3 s) -> the last good server config cached in localStorage;
 *     none cached -> today's behaviour.
 *   - steps 4/5 are reached only when the server answered 200 (it explicitly allows the new page)
 *     but left a key out. They never bypass the server's rollout gate.
 *
 * MF7 (2026-09-29, the owner's Pixel) added two switches, each ON by default under the same rules and OFF = the r14 page:
 *   hold    - with reconnect on, a silent tile HOLDS its socket instead of reconnecting: a launcher-r4 PC heals the phone
 *             itself (the page shows its words), and on the direct route an unanswered liveness check backs off. Off = the
 *             old probe -> close -> reconnect (lib/scrcpyStream.js, HOLD).
 *   reprobe - with the AUTO route, a tile on the server route tries the direct route again in the background and switches
 *             only after the direct stream's first frame (lib/streamManager.js, AUTO RE-PROBE). Off = sticky until Refresh.
 * The r15v server's config does not send them yet; a later server may (sanitizeServerConfig accepts booleans). Until then
 * ?hold=off / ?reprobe=off (stored in this browser) turn them off.
 */

export const LS_KEYS = Object.freeze({
  transport: 'necro.monitor.transport',
  reconnect: 'necro.monitor.reconnect',
  states: 'necro.monitor.states',
  repair: 'necro.monitor.repair',
  hold: 'necro.monitor.hold',
  reprobe: 'necro.monitor.reprobe',
});
export const LS_SERVER_CACHE = 'necro.monitor.serverConfig';

export const TRANSPORTS = ['local', 'remote', 'auto'];
export const SWITCH_KEYS = ['reconnect', 'states', 'repair', 'hold', 'reprobe'];
export const CONFIG_TIMEOUT_MS = 3000;
export const PROBE_TIMEOUT_MS = 1000;
/** Marker the built bundle must contain (production build rule: verify the bytes, not the source). */
export const RESOLVER_MARKER = 'necro-monitor-route-resolver-v1';

/** Today's page (launcher r3 / server e48e459 / V3 as deployed). */
export const TODAY = Object.freeze({ transport: 'local', reconnect: false, states: false, repair: false, hold: false, reprobe: false });

/** Server-route grid tier (owner D5). The expanded view stays at QUALITY.high on every route. */
export const DEFAULT_REMOTE_LOW = Object.freeze({ bitrate: 400000, maxFps: 10, maxSize: 360, iFrameInterval: 10 });

const OFF_WORDS = new Set(['off', '0', 'false', 'no']);
const ON_WORDS = new Set(['on', '1', 'true', 'yes']);
const CLEAR_WORDS = new Set(['default', 'reset', 'clear']);
export const CLEAR = 'clear';

const norm = (v) => (v === null || v === undefined ? '' : String(v).trim().toLowerCase());

/** 'local' | 'remote' | 'auto' | CLEAR | null. 'direct' is accepted for 'local'. */
export function parseTransport(v) {
  const s = norm(v);
  if (!s) return null;
  if (CLEAR_WORDS.has(s)) return CLEAR;
  if (s === 'direct') return 'local';
  return TRANSPORTS.includes(s) ? s : null;
}

/** true | false | CLEAR | null. */
export function parseSwitch(v) {
  const s = norm(v);
  if (!s) return null;
  if (CLEAR_WORDS.has(s)) return CLEAR;
  if (ON_WORDS.has(s)) return true;
  if (OFF_WORDS.has(s)) return false;
  return null;
}

const parseKey = (key, v) => (key === 'transport' ? parseTransport(v) : parseSwitch(v));

/** localStorage wrapper that never throws (private windows / blocked site data throw on access). */
export function safeStorage(ls) {
  const get = () => {
    try { return ls !== undefined ? ls : window.localStorage; } catch (e) { return null; }
  };
  return {
    get(k) { try { const s = get(); return s ? s.getItem(k) : null; } catch (e) { return null; } },
    set(k, v) { try { const s = get(); if (s) s.setItem(k, v); } catch (e) { /* ignore */ } },
    remove(k) { try { const s = get(); if (s) s.removeItem(k); } catch (e) { /* ignore */ } },
  };
}

function readUrl(search) {
  const out = {};
  let params;
  try { params = new URLSearchParams(search || ''); } catch (e) { return out; }
  for (const key of ['transport', ...SWITCH_KEYS]) {
    if (!params.has(key)) continue;
    const v = parseKey(key, params.get(key));
    if (v !== null) out[key] = v;
  }
  return out;
}

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** Validate a server answer. Unknown or malformed keys are dropped, never guessed. */
export function sanitizeServerConfig(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  const t = parseTransport(raw.transport);
  if (t && t !== CLEAR) out.transport = t;
  for (const k of SWITCH_KEYS) {
    if (typeof raw[k] === 'boolean') out[k] = raw[k];
  }
  const cd = Number(raw.repairCooldownSec);
  if (Number.isFinite(cd) && cd > 0) out.repairCooldownSec = clamp(Math.round(cd), 10, 3600);
  const probeMs = Number(raw.autoProbeMs);
  if (Number.isFinite(probeMs) && probeMs > 0) out.autoProbeMs = clamp(Math.round(probeMs), 200, 3000);
  // The server-route tiers. The r15v server answers remoteQuality {grid, expanded} (and clamps the
  // 36-byte settings to the same caps on its side); `remoteLow` is accepted as the grid tier too.
  const rq = raw.remoteQuality && typeof raw.remoteQuality === 'object' ? raw.remoteQuality : {};
  const grid = tierOf(rq.grid || raw.remoteLow, 4000000);
  if (grid) out.remoteLow = grid;
  const expanded = tierOf(rq.expanded, 20000000);
  if (expanded) out.remoteHigh = expanded;
  return out;
}

function tierOf(t, maxBitrate) {
  if (!t || typeof t !== 'object') return null;
  const b = Number(t.bitrate); const f = Number(t.maxFps); const s = Number(t.maxSize);
  if (!Number.isFinite(b) || !Number.isFinite(f) || !Number.isFinite(s)) return null;
  return {
    bitrate: clamp(Math.round(b), 100000, maxBitrate),
    maxFps: clamp(Math.round(f), 1, 60),
    maxSize: clamp(Math.round(s), 144, 2160),
    iFrameInterval: 10,
  };
}

function withTimeout(promise, ms) {
  let timer;
  return Promise.race([
    Promise.resolve().then(() => promise),
    new Promise((_, reject) => { timer = setTimeout(() => reject(Object.assign(new Error('timeout'), { code: 'TIMEOUT' })), ms); }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Ask the server, with the fallbacks above.
 * @returns {Promise<{status:'ok'|'old-server'|'signed-out'|'cached'|'unavailable', cfg:object, httpStatus?:number, detail?:string}>}
 */
export async function loadServerConfig(fetchServerConfig, storage, timeoutMs = CONFIG_TIMEOUT_MS) {
  const readCache = () => {
    try {
      const raw = storage.get(LS_SERVER_CACHE);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && parsed.cfg ? sanitizeServerConfig(parsed.cfg) : null;
    } catch (e) { return null; }
  };
  if (typeof fetchServerConfig !== 'function') {
    const c = readCache();
    return c ? { status: 'cached', cfg: c, detail: 'no config call available' } : { status: 'unavailable', cfg: { ...TODAY }, detail: 'no config call available' };
  }
  let res;
  try {
    res = await withTimeout(fetchServerConfig(), timeoutMs);
  } catch (err) {
    const http = err && err.response ? err.response.status : undefined;
    if (http === 404) return { status: 'old-server', cfg: { ...TODAY }, httpStatus: 404 };
    if (http === 401) return { status: 'signed-out', cfg: { ...TODAY }, httpStatus: 401 };
    const detail = http ? `HTTP ${http}` : (err && (err.code === 'TIMEOUT' ? 'timeout' : err.message)) || 'error';
    const c = readCache();
    if (c) return { status: 'cached', cfg: c, httpStatus: http, detail };
    return { status: 'unavailable', cfg: { ...TODAY }, httpStatus: http, detail };
  }
  const body = res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
  const cfg = sanitizeServerConfig(body);
  if (!cfg.transport) {
    // A 200 that is not the config (an HTML fallback page, an empty body, a proxy's answer) must not
    // switch the new page on: it is treated like an unreachable server, never as "allowed".
    const detail = `answer without a valid transport (${typeof body === 'string' ? 'text' : typeof body})`;
    const c = readCache();
    if (c) return { status: 'cached', cfg: c, httpStatus: res && res.status, detail };
    return { status: 'unavailable', cfg: { ...TODAY }, httpStatus: res && res.status, detail };
  }
  storage.set(LS_SERVER_CACHE, JSON.stringify({ at: Date.now(), cfg }));
  return { status: 'ok', cfg, httpStatus: (res && res.status) || 200 };
}

/**
 * Resolve the Monitor's video config. Never rejects.
 *
 * @param {object} deps
 * @param {string}   [deps.search]             window.location.search
 * @param {object}   [deps.storage]            safeStorage() by default
 * @param {Function} [deps.fetchServerConfig]  () => Promise<axios response> (GET /uiapi/bm/video/config)
 * @param {string}   [deps.envTransport]       REACT_APP_MONITOR_TRANSPORT
 * @param {string}   [deps.nodeEnv]            NODE_ENV
 * @param {number}   [deps.timeoutMs]
 */
export async function resolveMonitorConfig(deps = {}) {
  const storage = deps.storage || safeStorage();
  const url = readUrl(deps.search);

  // 1 -> 2: a URL value is stored; CLEAR removes the stored value.
  for (const [key, v] of Object.entries(url)) {
    if (v === CLEAR) storage.remove(LS_KEYS[key]);
    else storage.set(LS_KEYS[key], key === 'transport' ? v : (v ? 'on' : 'off'));
  }
  const stored = {};
  for (const key of ['transport', ...SWITCH_KEYS]) {
    const v = parseKey(key, storage.get(LS_KEYS[key]));
    if (v !== null && v !== CLEAR) stored[key] = v;
  }

  const server = await loadServerConfig(deps.fetchServerConfig, storage, deps.timeoutMs ?? CONFIG_TIMEOUT_MS);
  const serverAllowsNew = server.status === 'ok' || server.status === 'cached';
  const production = deps.nodeEnv === 'production';
  const buildDefault = { transport: production ? 'auto' : 'local', reconnect: true, states: true, repair: true, hold: true, reprobe: true };
  const env = parseTransport(deps.envTransport);

  const out = { sources: {}, overrides: [], server };
  for (const key of ['transport', ...SWITCH_KEYS]) {
    const fromUrl = url[key] !== undefined && url[key] !== CLEAR;
    if (fromUrl || stored[key] !== undefined) {
      out[key] = fromUrl ? url[key] : stored[key];
      out.sources[key] = fromUrl ? 'url' : 'browser';
      out.overrides.push({ key, value: out[key] });
    } else if (server.cfg[key] !== undefined) {
      out[key] = server.cfg[key];
      out.sources[key] = server.status === 'ok' ? 'server' : server.status === 'cached' ? 'server-cache' : server.status;
    } else if (serverAllowsNew && key === 'transport' && env && env !== CLEAR) {
      out[key] = env;
      out.sources[key] = 'build-env';
    } else if (serverAllowsNew) {
      out[key] = buildDefault[key];
      out.sources[key] = 'default';
    } else {
      out[key] = TODAY[key];
      out.sources[key] = server.status;
    }
  }
  // MF7: hold and reprobe are parts of self-heal - with reconnect off (the master-off answer, an older server, a browser
  // override) they are off too, whatever their own source said, so "reconnect off" is still exactly the r14 page.
  for (const key of ['hold', 'reprobe']) {
    if (!out.reconnect && out[key]) {
      out[key] = false;
      out.sources[key] = 'reconnect-off';
    }
  }
  out.repairCooldownSec = server.cfg.repairCooldownSec || 60;
  out.remoteLow = server.cfg.remoteLow || { ...DEFAULT_REMOTE_LOW };
  out.remoteHigh = server.cfg.remoteHigh || null; // null = QUALITY.high (sharp), as today
  out.autoProbeMs = server.cfg.autoProbeMs || PROBE_TIMEOUT_MS;
  out.marker = RESOLVER_MARKER;
  return out;
}

/** Remove every stored override (the page's Reset button). */
export function clearStoredOverrides(storage = safeStorage()) {
  for (const k of Object.values(LS_KEYS)) storage.remove(k);
}

/**
 * The AUTO route's probe: can this browser open a WebSocket to the relay on its own PC?
 * The same mechanism the tiles use (a WebSocket, not fetch: the relay's /bridge/* HTTP API refuses
 * non-loopback origins, and a WebSocket upgrade is exactly what a direct tile needs). The action is a
 * harmless unknown one: the stock server answers the upgrade and closes 4002 "Unsupported request".
 * Resolves {ok, why, ms}; never rejects. The socket is closed as soon as the verdict is known.
 */
export function probeLocalRelay({ base = 'ws://localhost:6847', timeoutMs = PROBE_TIMEOUT_MS, WebSocketImpl } = {}) {
  const WS = WebSocketImpl || (typeof WebSocket !== 'undefined' ? WebSocket : null);
  const t0 = Date.now();
  return new Promise((resolve) => {
    let done = false;
    let ws = null;
    let timer = null;
    const finish = (ok, why) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      if (ws) {
        try { ws.onopen = null; ws.onerror = null; ws.onclose = null; ws.close(); } catch (e) { /* noop */ }
      }
      resolve({ ok, why, ms: Date.now() - t0 });
    };
    if (!WS) { finish(false, 'no WebSocket'); return; }
    timer = setTimeout(() => finish(false, 'timeout'), timeoutMs);
    try {
      ws = new WS(`${String(base).replace(/\/+$/, '')}/?action=necro-probe`);
    } catch (e) {
      finish(false, 'cannot open: ' + (e && e.message ? e.message : e));
      return;
    }
    ws.onopen = () => finish(true, 'open');
    ws.onerror = () => finish(false, 'error');
    ws.onclose = (ev) => finish(false, 'closed ' + (ev && ev.code != null ? ev.code : ''));
  });
}

/**
 * The page-level route for a resolved transport: 'local' (direct) or 'remote' (through the server).
 * `auto` probes; the result also says whether per-tile fallback to the server is allowed.
 * @returns {Promise<{route:'local'|'remote', autoFallback:boolean, probe:object|null}>}
 */
export async function chooseRoute(transport, probeFn = probeLocalRelay) {
  if (transport === 'remote') return { route: 'remote', autoFallback: false, probe: null };
  if (transport !== 'auto') return { route: 'local', autoFallback: false, probe: null };
  let probe;
  try { probe = await probeFn(); } catch (e) { probe = { ok: false, why: 'probe threw' }; }
  return probe && probe.ok
    ? { route: 'local', autoFallback: true, probe }
    : { route: 'remote', autoFallback: false, probe };
}
